import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faNewspaper,
  faPlay,
  faExternalLinkAlt,
  faGlobe,
  faEye,
  faUsers,
  faStar,
  faHeart,
  faRocket,
  faCheckCircle,
  faArrowRight,
  faShieldAlt,
  faLightbulb,
  faGem
} from '@fortawesome/free-solid-svg-icons';

const WebsiteOverview = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const staggerItem = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 },
    transition: { duration: 0.5 }
  };

  const platforms = [
    {
      id: 1,
      name: "IINSAF News",
      shortName: "iinsaf news",
      description: "Your trusted source for news and updates",
      url: "https://www.iinsaf.news/",
      icon: faNewspaper,
      color: "from-blue-500 to-blue-600",
      bgColor: "from-blue-50 to-blue-100",
      features: ["Breaking News", "Verified Sources", "Real-time Updates", "Community Driven"],
      stats: { users: "10K+", articles: "5K+", verified: "99%" }
    },
    {
      id: 2,
      name: "IINSAFLIX",
      shortName: "iinsaflix",
      description: "Your entertainment destination",
      url: "https://iinsaflix.com/",
      icon: faPlay,
      color: "from-red-500 to-red-600",
      bgColor: "from-red-50 to-red-100",
      features: ["Streaming Content", "Original Series", "HD Quality", "Multi-device"],
      stats: { viewers: "50K+", content: "1K+", quality: "HD" }
    }
  ];

  const stats = [
    { number: "60K+", label: "Total Users", icon: faUsers },
    { number: "6K+", label: "Content Items", icon: faNewspaper },
    { number: "99%", label: "Uptime", icon: faShieldAlt },
    { number: "24/7", label: "Support", icon: faHeart }
  ];

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-white to-blue-50 pt-20 pb-8 px-2 sm:px-4">
      {/* Header Section */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: isVisible ? 1 : 0, scale: isVisible ? 1 : 0.95 }}
        transition={{ duration: 0.8 }}
        className="w-full max-w-7xl mx-auto mb-16"
      >
        <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 p-8 sm:p-12">
          <div className="text-center">
            <div className="inline-block bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-2 rounded-full text-sm font-semibold mb-4 shadow-lg">
              OUR PLATFORMS
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">
              Explore Our <span className="bg-gradient-to-r from-blue-500 to-blue-600 bg-clip-text text-transparent">Digital</span> Ecosystem
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-blue-600 mx-auto rounded-full mb-6"></div>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Discover our comprehensive digital platforms dedicated to news, entertainment, and community engagement
            </p>
          </div>
        </div>
      </motion.div>

      {/* Platforms Section */}
      <div className="w-full max-w-7xl mx-auto mb-16">
        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="grid grid-cols-1 lg:grid-cols-2 gap-8"
        >
          {platforms.map((platform, index) => (
            <motion.div
              key={platform.id}
              variants={staggerItem}
              className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 overflow-hidden hover:shadow-3xl transition-all duration-300 group"
            >
              {/* Platform Header */}
              <div className={`bg-gradient-to-r ${platform.color} p-6 sm:p-8`}>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <FontAwesomeIcon icon={platform.icon} className="text-white text-2xl" />
                  </div>
                  <div>
                    <h3 className="text-2xl sm:text-3xl font-bold text-white mb-1">
                      <span className="text-red-200"></span>{platform.shortName}
                    </h3>
                    <p className="text-white/90 text-sm">{platform.description}</p>
                  </div>
                </div>

                {/* Platform Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-lg font-bold text-white">{platform.stats.users || platform.stats.viewers}</div>
                    <div className="text-xs text-white/80">Users</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-white">{platform.stats.articles || platform.stats.content}</div>
                    <div className="text-xs text-white/80">Content</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-white">{platform.stats.verified || platform.stats.quality}</div>
                    <div className="text-xs text-white/80">Quality</div>
                  </div>
                </div>
              </div>

              {/* Platform Features */}
              <div className="p-6 sm:p-8">
                <h4 className="text-lg font-bold text-gray-800 mb-4">Key Features</h4>
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {platform.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 text-sm" />
                      <span className="text-sm text-gray-600">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Platform Preview */}
                <div className="relative overflow-hidden bg-gray-100 rounded-2xl mb-6" style={{ height: '300px' }}>
                  <iframe
                    src={platform.url}
                    title={platform.name}
                    className="w-full h-full"
                    loading="lazy"
                    sandbox="allow-same-origin allow-scripts"
                  ></iframe>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>

                {/* Visit Button */}
                <a 
                  href={platform.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={`inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r ${platform.color} text-white rounded-xl hover:shadow-lg transition-all duration-300 hover:scale-105 font-semibold`}
                >
                  <FontAwesomeIcon icon={faExternalLinkAlt} className="text-sm" />
                  Visit Website
                  <FontAwesomeIcon icon={faArrowRight} className="text-sm" />
                </a>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Stats Section */}
      <div className="w-full max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 p-8 sm:p-12"
        >
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-4">
              Platform <span className="bg-gradient-to-r from-blue-500 to-blue-600 bg-clip-text text-transparent">Statistics</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Numbers that showcase our growing digital ecosystem and user engagement
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center group"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <FontAwesomeIcon icon={stat.icon} className="text-white text-xl" />
                </div>
                <div className="text-3xl sm:text-4xl font-bold text-gray-800 mb-2">{stat.number}</div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default WebsiteOverview;